import React, { useState } from 'react';
import { useTheme } from '../../context/ThemeContext';
import { Button } from '../ui/Button';
import { Users, User, Shield, ChevronRight, Play } from 'lucide-react';

export default function Demo() {
  const { isDark } = useTheme();
  const [activeView, setActiveView] = useState('agent');

  const views = {
    agent: {
      title: "Vue Agent",
      description: "L'agent voit ses stats, gère ses pauses, suit ses commissions",
      image: "/images/agent-view.png"
    },
    manager: {
      title: "Vue Manager",
      description: "Le manager supervise toute l'équipe en temps réel",
      image: "/images/manager-view.png"
    },
    admin: {
      title: "Vue Admin",
      description: "L'admin configure les objectifs et les commissions",
      image: "/images/admin-view.png"
    }
  };

  return (
    <section id="demo" className="py-20">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section header */}
        <div className="text-center max-w-3xl mx-auto mb-16">
          <h2 className={`text-3xl lg:text-4xl font-bold mb-4 ${
            isDark ? 'text-white' : 'text-gray-900'
          }`}>
            Découvrez la plateforme
          </h2>
          <p className={`text-lg ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
            Une interface pensée pour chaque rôle
          </p>
        </div>

        {/* Sélecteur de vue */}
        <div className="flex justify-center mb-12">
          <div className={`inline-flex p-1 rounded-xl ${
            isDark ? 'bg-gray-900 border border-gray-800' : 'bg-gray-100 border border-gray-200'
          }`}>
            {['agent', 'manager', 'admin'].map((role) => (
              <button
                key={role}
                onClick={() => setActiveView(role)}
                className={`
                  px-6 py-3 text-sm font-medium rounded-lg transition-all duration-200
                  ${activeView === role
                    ? isDark
                      ? 'bg-gray-800 text-white'
                      : 'bg-white text-gray-900 shadow-sm'
                    : isDark
                      ? 'text-gray-400 hover:text-gray-200'
                      : 'text-gray-500 hover:text-gray-700'
                  }
                `}
              >
                {role === 'agent' && <Users className="w-4 h-4 inline mr-2" />}
                {role === 'manager' && <User className="w-4 h-4 inline mr-2" />}
                {role === 'admin' && <Shield className="w-4 h-4 inline mr-2" />}
                {role.charAt(0).toUpperCase() + role.slice(1)}
              </button>
            ))}
          </div>
        </div>

        {/* Démo visuelle */}
        <div className="grid lg:grid-cols-2 gap-12 items-center">
          {/* Image / mockup */}
          <div className="relative order-2 lg:order-1">
            <div className={`absolute inset-0 bg-gradient-to-r from-primary-500 to-purple-600 rounded-3xl blur-3xl opacity-20`}></div>
            <div className={`relative rounded-2xl overflow-hidden shadow-2xl border ${
              isDark ? 'border-gray-800' : 'border-gray-200'
            }`}>
              <img 
                src={views[activeView].image} 
                alt={views[activeView].title}
                className="w-full h-auto"
                onError={(e) => {
                  e.target.src = `https://placehold.co/1200x800/1f2937/ffffff?text=${views[activeView].title}`;
                }}
              />
            </div>
            
            {/* Badge "Live" */}
            <div className="absolute top-4 right-4 flex items-center gap-2 px-3 py-1.5 bg-green-500 text-white text-sm font-medium rounded-full">
              <span className="relative flex h-2 w-2">
                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-white opacity-75"></span>
                <span className="relative inline-flex rounded-full h-2 w-2 bg-white"></span>
              </span>
              Live
            </div>
          </div>

          {/* Description */}
          <div className="order-1 lg:order-2">
            <h3 className={`text-2xl font-bold mb-4 ${
              isDark ? 'text-white' : 'text-gray-900'
            }`}>
              {views[activeView].title}
            </h3>
            <p className={`text-lg mb-6 ${isDark ? 'text-gray-300' : 'text-gray-600'}`}>
              {views[activeView].description}
            </p>
            
            <ul className="space-y-4 mb-8">
              <li className="flex items-center gap-3">
                <div className="w-5 h-5 rounded-full bg-green-100 dark:bg-green-900/30 flex items-center justify-center">
                  <div className="w-2 h-2 rounded-full bg-green-500"></div>
                </div>
                <span className={isDark ? 'text-gray-300' : 'text-gray-600'}>
                  Données synchronisées en temps réel
                </span>
              </li>
              <li className="flex items-center gap-3">
                <div className="w-5 h-5 rounded-full bg-green-100 dark:bg-green-900/30 flex items-center justify-center">
                  <div className="w-2 h-2 rounded-full bg-green-500"></div>
                </div>
                <span className={isDark ? 'text-gray-300' : 'text-gray-600'}>
                  Interface intuitive et premium
                </span>
              </li>
              <li className="flex items-center gap-3">
                <div className="w-5 h-5 rounded-full bg-green-100 dark:bg-green-900/30 flex items-center justify-center">
                  <div className="w-2 h-2 rounded-full bg-green-500"></div>
                </div>
                <span className={isDark ? 'text-gray-300' : 'text-gray-600'}>
                  Toutes les fonctionnalités incluses
                </span>
              </li>
            </ul>

            <Button size="lg" className="group">
              Voir une démo complète
              <ChevronRight className="w-4 h-4 ml-2 group-hover:translate-x-1 transition-transform" />
            </Button>
          </div>
        </div>
      </div>
    </section>
  );
}